import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import querystring from "query-string";

import LoadingOverlay from "./LoadingOverlay";
import { handleError } from "@/util/errorHandler";
import {
  renderTotal,
  renderPaginationPanel,
  renderNoData,
} from "@/util/paginationHelper";
import { debounce, defaults } from "lodash";
import ExportReportButton from "@/views/Components/ExportReport/ExportReportButton";

class TableContainerWithLocation extends Component {
  state = {
    data: [],
    total: 0,
    isLoading: false,
  };

  /**
   * Get search parameters for making an API request
   *
   * @returns {Object}
   */
  getParams = () => {
    const params = querystring.parse(this.props.location.search);
    const defaultParams = {
      page: 0,
      size: 10,
    };

    params.size = params.sizePerPage;
    delete params.sizePerPage;

    return defaults(params, defaultParams, this.props.reportAdditionalParams);
  };

  createExportReportButton = () => {
    return (
      <ExportReportButton
        params={this.getParams()}
        requestReport={this.props.requestReport}
        disableDateRangePicker={this.props.reportWithoutDateRange}
      />
    );
  };

  loadAndStoreData = async () => {
    const { filter, sort, page, size: sizePerPage } = this.getParams();

    this.setState({ isLoading: true });

    try {
      const { data, total } = await this.props.loadData(
        page,
        sizePerPage,
        filter,
        sort,
      );
      this.setState({ data, total });
    } catch (e) {
      handleError(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handlePageChange = page => {
    if (Number.isInteger(page)) {
      this.handleSearchQueryChange({ page: page - 1 });
    }
  };

  handleUpdateCurrentPage = () => {
    this.loadAndStoreData();
  };

  handleSizePerPageChange = sizePerPage => {
    this.handleSearchQueryChange({ sizePerPage });
  };

  handleFilterChange = filter => {
    this.handleSearchQueryChange({ filter, page: 0 });
  };

  handleSortChange = (sortName, sortOrder) => {
    const sort = `${sortName},${sortOrder}`;
    this.handleSearchQueryChange({ sort });
  };

  componentDidMount() {
    if (this.props.autoLoad) {
      this.loadAndStoreData();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      this.loadAndStoreData();
    }
  }

  handleSearchQueryChange = newQueryParams => {
    const { history, location } = this.props;

    const oldSearchQuery = querystring.parse(location.search);
    const newSearchQuery = querystring.stringify({
      ...oldSearchQuery,
      ...newQueryParams,
    });

    history.push({
      ...location,
      search: newSearchQuery,
    });
  };

  render() {
    const { page: pageString, size: sizePerPageString } = this.getParams();

    const page = Number(pageString);
    const sizePerPage = Number(sizePerPageString);

    const options = {
      sizePerPageList: [
        {
          text: "5",
          value: 5,
        },
        {
          text: "10",
          value: 10,
        },
        {
          text: "25",
          value: 25,
        },
        {
          text: "50",
          value: 50,
        },
      ],
      onPageChange: this.handlePageChange,
      onSizePerPageList: this.handleSizePerPageChange,
      onSearchChange: debounce(this.handleFilterChange, 1000),
      onSortChange: this.handleSortChange,
      page: page + 1,
      sizePerPage,
      hidePageListOnlyOnePage: true,
      paginationShowsTotal: renderTotal,
      paginationPanel: renderPaginationPanel,
      noDataText: renderNoData(this.state.isLoading),
      exportCSVBtn: this.createExportReportButton,
    };

    const fetchInfo = { dataTotalSize: this.state.total };

    // TODO: Maybe move this loader into the table, as it is done here:
    // https://stackoverflow.com/questions/43338884/how-add-react-loading-in-react-bootstrap-table
    return (
      <div style={{ position: "relative" }}>
        <LoadingOverlay loading={this.state.isLoading} />
        {React.cloneElement(this.props.children, {
          options: { ...options, ...this.props.options },
          data: this.state.data,
          fetchInfo: fetchInfo,
        })}
      </div>
    );
  }
}

TableContainerWithLocation.defaultProps = {
  autoLoad: true,
  options: {},
  reportAdditionalParams: {},
};

TableContainerWithLocation.propTypes = {
  loadData: PropTypes.func,
  requestReport: PropTypes.func,
  reportWithoutDateRange: PropTypes.bool,
  reportAdditionalParams: PropTypes.object,
  children: PropTypes.element.isRequired,
  autoLoad: PropTypes.bool,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withRouter(TableContainerWithLocation);
